import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  HardDrive, Database, RefreshCw, Search, Filter, Download, Trash2,
  ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2, FileText, FileSpreadsheet,
  Image as ImageIcon, X, Lock, FileCheck2,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const MODULE_LABELS = {
  revenue: 'Revenue', expenses: 'Expenses', suppliers: 'Suppliers',
  reconciliation: 'Reconciliation', recipes: 'Recipes', menu: 'Menu',
  menu_performance: 'Menu Performance', mandi: 'Mandi', reviews: 'Reviews',
  pos: 'Generic POS', auto: 'Auto-routing', unknown: 'Unknown',
};

const STATUS_COLORS = {
  processed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-rose-100 text-rose-800',
  processing: 'bg-blue-100 text-blue-800',
  lost: 'bg-amber-100 text-amber-800',
};

const fmtBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: '2-digit', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fileIcon = (name = '') => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return <FileText className="h-4 w-4 text-rose-600 flex-shrink-0" />;
  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return <ImageIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />;
  return <FileSpreadsheet className="h-4 w-4 text-emerald-600 flex-shrink-0" />;
};

export const AdminFiles = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [filterQ, setFilterQ] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);  // {file, cascade}

  const load = useCallback(async (newSkip = 0) => {
    setLoading(true); setError('');
    try {
      const params = { limit: PAGE_SIZE, skip: newSkip };
      if (filterQ.trim()) params.q = filterQ.trim();
      if (filterModule) params.module = filterModule;
      if (filterStatus) params.status = filterStatus;
      const { data } = await axios.get(`${API}/admin/files`, { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setSkip(newSkip);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load files');
    } finally { setLoading(false); }
  }, [filterQ, filterModule, filterStatus]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/files/storage-summary`);
      setSummary(data);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { load(0); loadSummary(); }, [load, loadSummary]);

  const onFilter = (e) => { e.preventDefault(); load(0); };

  const handleDownload = async (file) => {
    try {
      const resp = await axios.get(`${API}/admin/files/${file.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = file.original_filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Download failed');
    }
  };

  const handleRepairReport = async (file) => {
    try {
      const resp = await axios.get(`${API}/reports/repair-report/${file.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const base = (file.original_filename || 'upload').replace(/\.[^.]+$/, '');
      a.download = `repair_report_${base}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) {
      // If the file wasn't processed by AI Smart Upload there is no repair_log,
      // but the endpoint still renders a "perfect run" report. So a 404 here
      // almost certainly means the upload record itself is gone.
      setError(err?.response?.data?.detail || 'Repair Report generation failed');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const { file, cascade } = confirmDelete;
    try {
      await axios.delete(`${API}/admin/files/${file.id}`, { params: { cascade } });
      setConfirmDelete(null);
      load(skip);
      loadSummary();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-files-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <HardDrive className="h-6 w-6" /> File Storage (Admin)
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Cross-client file browser · backed by MongoDB GridFS · every download &amp; delete is audit-logged.
          </p>
        </div>
        <Button onClick={() => { load(skip); loadSummary(); }} variant="outline" size="sm" data-testid="admin-files-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </header>

      {/* Summary KPIs */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="storage-summary">
          <Kpi
            label="Total Storage Used"
            value={fmtBytes(summary.totals?.bytes || 0)}
            sub={`${(summary.totals?.files || 0).toLocaleString()} files in GridFS`}
            Icon={Database}
            color="text-primary"
          />
          <Kpi
            label="Top Client"
            value={summary.per_client?.[0]?.client_name || '—'}
            sub={`${fmtBytes(summary.per_client?.[0]?.bytes || 0)} · ${summary.per_client?.[0]?.files || 0} files`}
            Icon={CheckCircle2}
            color="text-emerald-700"
          />
          <Kpi
            label="Top Module"
            value={MODULE_LABELS[summary.per_module?.[0]?.module] || summary.per_module?.[0]?.module || '—'}
            sub={`${fmtBytes(summary.per_module?.[0]?.bytes || 0)} · ${summary.per_module?.[0]?.files || 0} files`}
            Icon={Filter}
            color="text-indigo-700"
          />
        </div>
      )}

      {/* Filter bar */}
      <form onSubmit={onFilter} className="bg-white border border-border rounded-md p-4 flex flex-wrap items-end gap-3" data-testid="admin-files-filters">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">Filename contains</label>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={filterQ}
              onChange={(e) => setFilterQ(e.target.value)}
              placeholder="e.g. spiceroute, december, supplier"
              className="w-full border border-border rounded pl-7 pr-2 py-1.5 text-sm"
              data-testid="admin-files-search"
            />
          </div>
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">Module</label>
          <select
            value={filterModule}
            onChange={(e) => setFilterModule(e.target.value)}
            className="w-full border border-border rounded px-2 py-1.5 text-sm"
            data-testid="admin-files-filter-module"
          >
            <option value="">All modules</option>
            {Object.entries(MODULE_LABELS).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[120px]">
          <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full border border-border rounded px-2 py-1.5 text-sm"
            data-testid="admin-files-filter-status"
          >
            <option value="">Any</option>
            <option value="processed">Processed</option>
            <option value="failed">Failed</option>
            <option value="lost">Lost</option>
          </select>
        </div>
        <Button type="submit" size="sm" data-testid="admin-files-apply">
          <Filter className="h-3.5 w-3.5 mr-1" /> Apply
        </Button>
      </form>

      {/* Table */}
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <div className="px-4 py-2 bg-background-secondary text-[11px] font-semibold uppercase tracking-wider text-text-secondary flex justify-between">
          <span>Files ({total.toLocaleString()})</span>
          <span className="text-text-muted">Showing {rows.length} · page {Math.floor(skip / PAGE_SIZE) + 1}</span>
        </div>
        {error && (
          <div className="p-4 text-sm text-state-error bg-rose-50 border-b border-border flex items-start gap-2" data-testid="admin-files-error">
            <AlertTriangle className="h-4 w-4 mt-0.5" /> <span>{error}</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-background-secondary text-text-secondary">
              <tr>
                <th className="text-left px-3 py-1.5 font-semibold">Filename</th>
                <th className="text-left px-3 py-1.5 font-semibold">Client</th>
                <th className="text-left px-3 py-1.5 font-semibold">Module</th>
                <th className="text-left px-3 py-1.5 font-semibold">Size</th>
                <th className="text-left px-3 py-1.5 font-semibold">Rows</th>
                <th className="text-left px-3 py-1.5 font-semibold">Status</th>
                <th className="text-left px-3 py-1.5 font-semibold">Security</th>
                <th className="text-left px-3 py-1.5 font-semibold">Uploaded</th>
                <th className="text-right px-3 py-1.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="9" className="text-center py-12 text-text-muted">No files match the current filters.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-background-secondary" data-testid={`admin-file-row-${r.id}`}>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2 max-w-[280px]">
                      {fileIcon(r.original_filename)}
                      <span className="text-text-primary truncate" title={r.original_filename}>
                        {r.original_filename}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-text-primary">{r.client_name || r.client_id}</td>
                  <td className="px-3 py-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold bg-slate-100 px-1.5 py-0.5 rounded">
                      {MODULE_LABELS[r.module] || r.module || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-text-muted">{fmtBytes(r.size_bytes)}</td>
                  <td className="px-3 py-1.5 font-mono text-text-primary">{r.records_count ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-700'}`}>
                      {r.status || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    {r.encrypted ? (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200"
                        title={`AES-256-GCM · key fp ${r.encryption_key_fp || ''}`}
                        data-testid="admin-file-encrypted-badge"
                      >
                        <Lock className="h-3 w-3" /> AES-256
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold text-[10px] bg-amber-50 text-amber-800 border border-amber-200"
                        title="Plaintext — run encrypt_existing_files migration"
                      >
                        <AlertTriangle className="h-3 w-3" /> plain
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-text-muted whitespace-nowrap">{fmtTime(r.uploaded_at)}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {r.status === 'processed' && (
                      <button
                        onClick={() => handleRepairReport(r)}
                        className="text-emerald-700 hover:text-emerald-800 p-1 mr-1"
                        title="Download Repair Report (data-quality audit PDF)"
                        data-testid={`admin-file-repair-report-${r.id}`}
                      >
                        <FileCheck2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(r)}
                      className="text-primary hover:text-primary-hover p-1 mr-1"
                      title="Download original file"
                      data-testid={`admin-file-download-${r.id}`}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete({ file: r, cascade: false })}
                      className="text-rose-600 hover:text-rose-700 p-1"
                      title="Delete"
                      data-testid={`admin-file-delete-${r.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-3 py-2 bg-background-secondary border-t border-border flex items-center justify-between">
          <Button
            onClick={() => load(Math.max(0, skip - PAGE_SIZE))}
            disabled={skip === 0 || loading}
            variant="outline" size="sm"
            data-testid="admin-files-prev"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </Button>
          <span className="text-[11px] text-text-muted">
            {total === 0 ? '0' : skip + 1}–{Math.min(skip + rows.length, total)} of {total.toLocaleString()}
          </span>
          <Button
            onClick={() => load(skip + PAGE_SIZE)}
            disabled={skip + rows.length >= total || loading}
            variant="outline" size="sm"
            data-testid="admin-files-next"
          >
            Next <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <DeleteModal
          file={confirmDelete.file}
          cascade={confirmDelete.cascade}
          setCascade={(v) => setConfirmDelete({ ...confirmDelete, cascade: v })}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

const Kpi = ({ label, value, sub, Icon, color }) => (
  <div className="bg-white border border-border rounded-md p-4 flex items-start gap-3" data-testid={`kpi-${label.replace(/ /g, '-').toLowerCase()}`}>
    <div className={`p-2 rounded-md bg-slate-50 ${color}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">{label}</div>
      <div className="text-lg font-bold text-text-primary truncate" title={value}>{value}</div>
      <div className="text-[11px] text-text-muted truncate" title={sub}>{sub}</div>
    </div>
  </div>
);

const DeleteModal = ({ file, cascade, setCascade, onConfirm, onCancel }) => {
  // Esc-to-close + click-outside (a11y polish)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      data-testid="admin-files-delete-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-rose-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Delete file?
            </h3>
            <p className="text-xs text-text-muted mt-1">This action cannot be undone. Press Esc to cancel.</p>
          </div>
          <button onClick={onCancel} className="text-text-muted hover:text-text-primary" data-testid="admin-files-delete-close" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="bg-slate-50 border border-border rounded p-3 text-xs space-y-1">
          <div className="font-semibold text-text-primary truncate">{file.original_filename}</div>
          <div className="text-text-muted">Client: {file.client_name || file.client_id}</div>
          <div className="text-text-muted">Module: {MODULE_LABELS[file.module] || file.module} · {fmtBytes(file.size_bytes)} · {file.records_count ?? 0} rows</div>
        </div>
        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={cascade}
            onChange={(e) => setCascade(e.target.checked)}
            className="mt-0.5"
            data-testid="admin-files-delete-cascade"
          />
          <span className="text-text-primary">
            <strong>Also remove imported rows</strong> (cascade delete) — wipes every record this file created across modules.
            Leave unchecked to keep the imported data and only delete the source file.
          </span>
        </label>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} data-testid="admin-files-delete-cancel">Cancel</Button>
          <Button
            onClick={onConfirm}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="admin-files-delete-confirm"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete{cascade ? ' + Cascade' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminFiles;
